import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Timeline from '@/components/Timeline'
import AssociateMemberCard from './accociate-member-card'
import PhotoHouseImg from '@/assets/images/photo-house.png'
import { RefObject, useRef } from 'react'
import { useClickAway } from 'react-use'
import IconButton from '@mui/material/IconButton'
import { SalesItem } from '@/types/sales'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Scrollbar } from 'react-scrollbars-custom'
import { truncateText } from '@/utils/helper'

interface SalesDetailsProps {
  svgSize: {
    width: number
    height: number
    x: number
    y: number
  }
  onClose: () => void
  salesDetail: SalesItem
  mapWrapperRef: RefObject<HTMLDivElement>
  onPrev: () => void
  onNext: () => void
  scrollProgress: [boolean, boolean]
}

const SalesDetails = ({
  svgSize,
  onClose,
  salesDetail,
  mapWrapperRef,
  onPrev,
  onNext,
  scrollProgress
}: SalesDetailsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  useClickAway(wrapperRef, (event) => {
    if (mapWrapperRef.current && mapWrapperRef.current.contains(event.target as Node)) {
      onClose()
    }
  })

  return (
    <>
      {svgSize.width > 0 && (
        <svg
          id="triangleSvg"
          width={svgSize.width}
          height={svgSize.height}
          style={{
            position: 'absolute',
            top: svgSize.y,
            right: svgSize.x,
            zIndex: 0,
            filter: 'drop-shadow(0 0 16px rgba(0,0,0,0.5))'
          }}
        ></svg>
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          width: 860,
          right: 12,
          height: 1180,
          zIndex: 2,
          px: 2.5,
          py: 2,
          borderRadius: '8px',
          backgroundColor: 'rgba(38,54,81,0.85)',
          boxShadow: '16px 8px 16px 0 rgba(0,0,0,0.5)'
        }}
        ref={wrapperRef}
      >
        <Stack direction="row" sx={{ mb: 3 }} alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3" mb={2.5}>
              {truncateText(salesDetail.sale_title, 44)}
            </Typography>
          </Stack>
          <Stack direction="row">
            <IconButton onClick={onPrev}>
              <ChevronLeftIcon htmlColor="#dde3f6" opacity={scrollProgress[0] ? 0.25 : 1} />
            </IconButton>
            <IconButton onClick={onNext}>
              <ChevronRightIcon htmlColor="#dde3f6" opacity={scrollProgress[1] ? 0.25 : 1} />
            </IconButton>
            <IconButton onClick={onClose} size={'large'}>
              <CloseIcon htmlColor="#fff" />
            </IconButton>
          </Stack>
        </Stack>
        <Grid container spacing={5}>
          <Grid item lg={6}>
            <Box
              borderRadius={1}
              sx={{
                boxShadow: '6px 2px 4px 0 rgba(0,0,0,0.25)',
                width: '100%',
                height: '218px',
                overflow: 'hidden',
                mb: 5
              }}
            >
              <img
                src={salesDetail.homesite_image_url || PhotoHouseImg}
                alt={salesDetail.sale_title}
                width="100%"
                height="218px"
                loading="lazy"
                style={{
                  objectFit: 'cover'
                }}
              />
            </Box>
            <Grid container columnSpacing={2} rowSpacing={'21px'}>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Sale price
                </Typography>
                <Typography variant="h3">${salesDetail.sale_price.toLocaleString()}</Typography>
              </Grid>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Price vs model avg.
                </Typography>
                <Typography
                  variant="h3"
                  color={salesDetail.price_vs_model_avg < 0 ? 'error' : 'text.tertiary'}
                >
                  {salesDetail.price_vs_model_avg <= 0
                    ? salesDetail.price_vs_model_avg
                    : `+ ${salesDetail.price_vs_model_avg}`}
                  %
                </Typography>
              </Grid>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Incentives
                </Typography>
                <Typography variant="h3">{salesDetail.incentive_percent}%</Typography>
              </Grid>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Realtor commission
                </Typography>
                <Typography variant="h3" color="text.tertiary">
                  {salesDetail.realtor_commission}%
                </Typography>
              </Grid>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  {salesDetail.home_type}
                </Typography>
                <Typography variant="h3">{salesDetail.bed_bath}</Typography>
              </Grid>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Home size
                </Typography>
                <Typography variant="h3">{salesDetail.home_size}</Typography>
              </Grid>
              {salesDetail.lot_size != 'Not Available' && (
                <Grid item lg={6} mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Lot size
                  </Typography>
                  <Typography variant="h3">
                    {salesDetail.lot_size === 'Not Available' ? '' : salesDetail.lot_size}
                  </Typography>
                </Grid>
              )}
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Days from completion
                </Typography>
                <Typography variant="h3" whiteSpace="nowrap">
                  {salesDetail.days_from_completion}
                </Typography>
              </Grid>
              <Grid item lg={6} mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Web content score (1-5)
                </Typography>
                <Typography variant="h3" whiteSpace="nowrap">
                  {salesDetail.web_content_score}
                </Typography>
              </Grid>
            </Grid>
            <Box overflow="hidden" height="360px" mt={3.5}>
              <Scrollbar noScrollX style={{ height: 270, width: '100%' }}>
                <Stack spacing="20px">
                  <Typography variant="caption" color="grey.400">
                    Associates
                  </Typography>
                  {!!salesDetail.associate_array?.length &&
                    salesDetail.associate_array.map((member, idx) => (
                      <AssociateMemberCard member={member} key={idx} />
                    ))}
                </Stack>
              </Scrollbar>
            </Box>
          </Grid>
          <Grid item lg={6}>
            {salesDetail.journey_array && !!salesDetail.journey_array?.length && (
              <Timeline journeyData={salesDetail.journey_array} />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default SalesDetails
